'use client';

import type { FC } from 'react';
import type { AuthorDto } from '@/queries/author/author.schema';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
// eslint-disable-next-line no-restricted-imports
import Link from 'next/link';
import { cn } from '@/utils/cn';
import { isEmpty } from '@/utils/objects.utils';
import { Icon } from '../Sprite';

interface AuthorCardProps {
  className?: string;
  author: AuthorDto;
}

export const AuthorCard: FC<AuthorCardProps> = ({ className, author }) => {
  const t = useTranslations('game.game-details.author');

  return (
    <div className={cn('flex gap-3 md:gap-6 p-4 md:py-6 md:flex-row flex-col md:px-5 bg-dark-violet-bottom rounded-[0.75rem]', className)}>

      {
        author.avatar && (
          <div className="relative h-35 w-35 shrink-0 aspect-square rounded-[0.75rem] overflow-hidden">
            <Image fill src={author.avatar.key ?? author.avatar.path} alt="Author" sizes="140px" />
          </div>
        )
      }

      <div className="flex gap-9.5 md:gap-6 flex-col md:max-w-137.75 w-full">
        <div className="flex gap-2 flex-col">
          <p className="text-light text-lg tracking-normal font-medium leading-5.5">
            {author.firstName}
            {' '}
            {author.lastName}
          </p>

          {
            author.description && (
              <p className="paragraph-s-regular tracking-normal text-violet-light">
                {author.description}
              </p>
            )
          }

          {
            !isEmpty(author?.pageContents) && (
              <p className="text-light paragraph-s-regular">
                {author?.pageContents?.length}
                {' '}
                {t('articles', { count: author?.pageContents?.length || 1 })}
              </p>
            )
          }
        </div>
        {
          !isEmpty(author.socialLinks) && (
            <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center">
              <div className="flex gap-2.5 text-light">
                {
                  author.socialLinks?.instagram && (
                    <Link target="_blank" rel="noopener noreferrer" href={author.socialLinks.instagram} className="hover:text-light text-c300 transition-colors">
                      <Icon name="instagram" className="w-6 h-6 " />
                    </Link>
                  )
                }
                {
                  author.socialLinks?.x && (
                    <Link target="_blank" rel="noopener noreferrer" href={author.socialLinks.x} className="hover:text-light text-c300 transition-colors">
                      <Icon name="twitter" className="w-6 h-6" />
                    </Link>
                  )
                }
                {
                  author.socialLinks?.linkedIn && (
                    <Link target="_blank" rel="noopener noreferrer" href={author.socialLinks.linkedIn} className="hover:text-light text-c300 transition-colors">
                      <Icon name="linkedin" className="w-6 h-6" />
                    </Link>
                  )
                }
              </div>

            </div>
          )
        }

      </div>
    </div>
  );
};
